import type { DetectionResult } from './get_detections'

// Pure, DOM-free formatting helpers for the export feature. Kept separate from
// useExportDetections (which touches `window`/`import.meta`) so they stay unit-testable.

const CSV_HEADERS = [
    'detection_uuid',
    'device_uuid',
    'device_id',
    'timestamp',
    'license_plate',
    'country',
    'direction',
    'category',
    'vehicle_type',
    'lpr_conf',
    'lpness',
    'render_url',
    'lp_url',
    'roi_url'
] as const

/** CSV-escape a single cell: ISO-format dates, and quote/double-quote when it contains `,` `"` or newline. */
const toCell = (value: unknown): string => {
    if (value == null) return ''
    const s = value instanceof Date ? value.toISOString() : String(value)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Serialize detections to a CSV string with a fixed header row. */
export const toCSV = (detections: DetectionResult[]): string => {
    const rows = detections.map((d) =>
        [
            d.detection_uuid,
            d.device_uuid,
            d.device_id,
            d.timestamp,
            d.license_plate,
            d.country,
            d.direction,
            d.category,
            d.vehicle_type,
            d.lpr_conf,
            d.lpness,
            d.images.render.url,
            d.images.lp.url,
            d.images.roi.url
        ]
            .map(toCell)
            .join(',')
    )
    return [CSV_HEADERS.join(','), ...rows].join('\n')
}

/**
 * Format a date as a filename-safe timestamp: `YYYY-MM-DD_HH-mm` (UTC). Colons are
 * illegal in Windows filenames, so the ISO time separators are swapped for `-`/`_`.
 */
export const toFileTimestamp = (date: Date): string =>
    date.toISOString().slice(0, 16).replace('T', '_').replace(':', '-')

/** File extension from a URL's path, defaulting to 'jpg'. */
export const extFromUrl = (url: string): string => {
    try {
        const name = new URL(url).pathname.split('/').filter(Boolean).pop() ?? ''
        const dot = name.lastIndexOf('.')
        if (dot > -1) return name.slice(dot + 1)
    } catch {
        // fall through
    }
    return 'jpg'
}

/** Make a license plate (or any value) safe to use as a single path segment. */
export const sanitizeSegment = (value: string): string => value.replace(/[/\\]/g, '_').trim() || 'unknown'

/**
 * Returns a stateful path de-duplicator. The same license plate can recur on a device,
 * so a repeated path gets a `_2`, `_3`, … suffix before its extension instead of colliding.
 */
export const makeUniquePath = (): ((path: string) => string) => {
    const used = new Set<string>()
    return (path: string): string => {
        if (!used.has(path)) {
            used.add(path)
            return path
        }
        const dot = path.lastIndexOf('.')
        const stem = dot > -1 ? path.slice(0, dot) : path
        const ext = dot > -1 ? path.slice(dot) : ''
        let n = 2
        while (used.has(`${stem}_${n}${ext}`)) n++
        const candidate = `${stem}_${n}${ext}`
        used.add(candidate)
        return candidate
    }
}
